import React from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  useTheme
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { V4_TABLE } from '../../../theme/v4Compat';
import { useTranslation } from 'react-i18next';
import TranslationSearch from '../TranslationSearch/component';
import { DEFAULT_ROWS_PAGE_BIG_OPTIONS, DEFAULT_ROWS_PER_PAGE_MIDDLE } from '../../../constants/constants';

interface i18nElement {
  key: 'string';
  value: 'string';
}

interface TranslationEditByFieldProps {
  searchValue: string;
  translations: i18nElement[];
  resetSearch: () => void;
  applySearch: () => void;
  setSearchValue: (value: string) => void;
  handleChangeField: (key: string, value: string) => void;
}

const TranslationEditByField: React.FC<any> = ({
  translations,
  resetSearch,
  searchValue,
  applySearch,
  setSearchValue,
  handleChangeField
}: TranslationEditByFieldProps) => {
  const styles = sxStyles(useTheme());
  const { t } = useTranslation();

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE_MIDDLE);

  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  return (
    <Paper elevation={1}>
      <TranslationSearch
        searchValue={searchValue}
        resetSearch={resetSearch}
        applySearch={applySearch}
        setSearchValue={setSearchValue}
      />

      <Table size="small" sx={V4_TABLE}>
        <TableHead>
          <TableRow>
            <TableCell variant="head">{t('f-trans-key')}</TableCell>
            <TableCell variant="head">{t('f-trans-value')}</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          <>
            {translations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(({ key, value }) => (
              <TableRow key={key} hover>
                <TableCell sx={styles.tableHeader}>{key}</TableCell>
                <TableCell>
                  <TextField
                    multiline
                    fullWidth
                    value={value}
                    type="string"
                    size="small"
                    id="i18n-value"
                    variant="outlined"
                    onChange={(e) => handleChangeField(key, e.target.value)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </>
        </TableBody>
        <TablePagination
          page={page}
          rowsPerPage={rowsPerPage}
          count={translations.length}
          onPageChange={handleChangePage}
          rowsPerPageOptions={DEFAULT_ROWS_PAGE_BIG_OPTIONS}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Table>
    </Paper>
  );
};

export default TranslationEditByField;

/** Was ./styles.ts (makeStyles); v9 `sx` values. */
const sxStyles = (_theme: Theme) =>
  ({
    tableHeader: {
      padding: '10px',
      margin: '10px'
    }
  }) as const;
